import { Box, Link, LinkBox, LinkOverlay, Stack, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import NextLink from 'next/link';
import * as React from 'react';

import { SearchHelpers } from '@/client/legacy-shared-modules/search/helpers';
import { IFileSearch, ViewableAttributesField } from '@/client/legacy-shared-modules/search/search-types';
import { CustomHighlight } from '@/components/CustomHighlight';
import { FormattedUserName } from '@/components/FormattedUserName';
import { FileIcon } from '@/imports/ui/chakra/feather';
import { formatDisplayName } from '@/utilities';

dayjs.extend(relativeTime);

export const File: React.FC<{ hit: IFileSearch }> = ({ hit }) => {
  const viewableAttributesField: ViewableAttributesField =
    (hit.adminGlobalViewable && 'adminGlobalViewable') ||
    (hit.membersWithMemberStatusGlobalViewable &&
      'membersWithMemberStatusGlobalViewable') ||
    (hit.loggedInGlobalViewable && 'loggedInGlobalViewable') ||
    'loggedInGlobalViewable'; // Provide default to avoid undefined
  
  const authorAttributes = hit[viewableAttributesField];

  // Format the display name using the utility function as fallback
  const displayName = React.useMemo(() => {
    if (!authorAttributes) {
      return hit.displayName || '';
    }

    return formatDisplayName({
      preferredName: authorAttributes.preferredName,
      prefix: authorAttributes.prefix,
      firstName: authorAttributes.firstName,
      lastName: authorAttributes.lastName,
      suffix: authorAttributes.suffix,
      nameTitle: authorAttributes.nameTitle,
    });
  }, [authorAttributes, hit.displayName]);

  return (
    <LinkBox
      as={Stack}
      direction="row"
      spacing="2"
      alignItems="center"
      p="2"
      bg="white"
      _hover={{ bg: 'gray.100' }}
      borderRadius="xl"
      cursor="pointer"
    >
      {/* TODO: get different icon depending on file type */}
      <FileIcon boxSize="8" />

      <Box>
        <LinkOverlay as={NextLink} href={SearchHelpers.getFileUrl(hit)}>
          <Text noOfLines={1} textStyle="bodyStrong">
            <CustomHighlight attribute="file.name" hit={hit} />
          </Text>
        </LinkOverlay>

        <Text noOfLines={1} textStyle="compact" color="gray.600">
          {dayjs(hit.dateCreated).fromNow()} by{' '}
          <Link as={NextLink} href={`/members/${hit?.author._id}`}>
            <FormattedUserName
              hit={hit}
              viewableAttributesField={viewableAttributesField}
              fallbackDisplayName={displayName}
            />
          </Link>
        </Text>
      </Box>
    </LinkBox>
  );
};
